import * as React from 'react'
import { ChevronsUpDown, Check, Volume2, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  elevenLabsApiClient,
  type VoiceOption,
} from '@/lib/api/elevenlabs.client'
import { toast } from 'sonner'

type VoiceSearchProps = {
  selectedVoice?: VoiceOption | null
  onVoiceSelect?: (voice: VoiceOption) => void | Promise<void>
  placeholder?: string
  buttonClassName?: string
  greetingText?: string
}

export function VoiceSearch({
  selectedVoice: initialSelectedVoice,
  onVoiceSelect,
  placeholder = 'Search voices...',
  buttonClassName,
  greetingText = 'Hello! How can I help you today?',
}: VoiceSearchProps) {
  const [selectedVoice, setSelectedVoice] = React.useState<VoiceOption | null>(
    initialSelectedVoice || null,
  )
  const [searchQuery, setSearchQuery] = React.useState('')
  const [voices, setVoices] = React.useState<VoiceOption[]>([])
  const [isLoading, setIsLoading] = React.useState(false)
  const [isOpen, setIsOpen] = React.useState(false)
  const [selectedIndex, setSelectedIndex] = React.useState(-1)
  const searchInputRef = React.useRef<HTMLInputElement>(null)
  const [playingVoiceId, setPlayingVoiceId] = React.useState<string | null>(
    null,
  )
  const [audioRef, setAudioRef] = React.useState<HTMLAudioElement | null>(null)
  const [loadingVoiceId, setLoadingVoiceId] = React.useState<string | null>(
    null,
  )

  // Sync with prop changes - direct update in render
  if (initialSelectedVoice !== selectedVoice) {
    setSelectedVoice(initialSelectedVoice || null)
  }

  // Debounced search function
  const debouncedSearch = React.useCallback(
    React.useMemo(() => {
      let timeoutId: NodeJS.Timeout
      return (query: string) => {
        clearTimeout(timeoutId)
        timeoutId = setTimeout(async () => {
          setIsLoading(true)
          try {
            const results = await elevenLabsApiClient.fetchVoices(query)
            setVoices(results)
          } catch (error) {
            console.error('Failed to fetch voices:', error)
            toast.error('Failed to fetch voices')
            setVoices([])
          } finally {
            setIsLoading(false)
          }
        }, 300)
      }
    }, []),
    [],
  )

  // Initial search on mount
  React.useEffect(() => {
    debouncedSearch('')
  }, [debouncedSearch])

  const stopAllAudio = React.useCallback(() => {
    if (audioRef) {
      audioRef.pause()
      audioRef.currentTime = 0
    }
    setPlayingVoiceId(null)
    setLoadingVoiceId(null)
  }, [audioRef])

  const handlePlayPreview = async (e: React.MouseEvent, voice: VoiceOption) => {
    e.stopPropagation()

    // If this voice is currently playing, stop it
    if (playingVoiceId === voice.id) {
      stopAllAudio()
      return
    }

    try {
      // Always stop any currently playing audio first
      stopAllAudio()

      setLoadingVoiceId(voice.id)

      const audio = new Audio(voice.previewUrl)
      setAudioRef(audio)

      audio.onended = () => {
        setPlayingVoiceId(null)
        setLoadingVoiceId(null)
      }

      audio.onloadeddata = () => {
        setLoadingVoiceId(null)
        setPlayingVoiceId(voice.id)
        audio.play().catch(() => {
          setPlayingVoiceId(null)
          setLoadingVoiceId(null)
          toast.error('Failed to play voice preview')
        })
      }

      audio.onerror = () => {
        setLoadingVoiceId(null)
        setPlayingVoiceId(null)
        toast.error('Failed to play voice preview')
      }
    } catch (error) {
      setLoadingVoiceId(null)
      setPlayingVoiceId(null)
      toast.error('Failed to play voice preview')
    }
  }

  const handlePlayGreeting = async (
    e: React.MouseEvent,
    voice: VoiceOption,
  ) => {
    e.stopPropagation()

    if (!greetingText.trim()) {
      toast.error('No greeting text available')
      return
    }

    // If this voice greeting is currently playing, stop it
    if (playingVoiceId === `${voice.id}-greeting`) {
      stopAllAudio()
      return
    }

    try {
      // Always stop any currently playing audio first
      stopAllAudio()

      setLoadingVoiceId(`${voice.id}-greeting`)

      const audioUrl = await elevenLabsApiClient.generateSpeech(
        voice.id,
        greetingText,
      )
      const audio = new Audio(audioUrl)
      setAudioRef(audio)

      audio.onended = () => {
        setPlayingVoiceId(null)
        setLoadingVoiceId(null)
        // Clean up the blob URL
        URL.revokeObjectURL(audioUrl)
      }

      audio.onloadeddata = () => {
        setLoadingVoiceId(null)
        setPlayingVoiceId(`${voice.id}-greeting`)
        audio.play().catch(() => {
          setPlayingVoiceId(null)
          setLoadingVoiceId(null)
          URL.revokeObjectURL(audioUrl)
          toast.error('Failed to play greeting')
        })
      }

      audio.onerror = () => {
        setLoadingVoiceId(null)
        setPlayingVoiceId(null)
        URL.revokeObjectURL(audioUrl)
        toast.error('Failed to play greeting')
      }
    } catch (error) {
      setLoadingVoiceId(null)
      setPlayingVoiceId(null)
      toast.error('Failed to generate greeting audio')
    }
  }

  const handleVoiceSelect = async (voice: VoiceOption) => {
    setSelectedVoice(voice)
    setSearchQuery('')
    setSelectedIndex(-1)
    setIsOpen(false)

    // Stop any playing audio
    stopAllAudio()

    await onVoiceSelect?.(voice)

    // Automatically play greeting with the selected voice
    if (greetingText.trim()) {
      // Small delay to ensure UI updates first
      handlePlayGreeting(new Event('click') as any, voice)
    }
  }

  const trigger = (
    <Button
      variant="outline"
      className={cn('w-full justify-between', buttonClassName)}
      role="combobox"
      aria-expanded={isOpen}
      aria-label="Select a voice"
    >
      <div className="flex items-center gap-2">
        <Volume2 className="h-4 w-4 text-muted-foreground" />
        <span className="truncate">
          {selectedVoice?.name || 'Select voice'}
        </span>
      </div>
      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
    </Button>
  )

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex(prev => (prev < voices.length - 1 ? prev + 1 : prev))
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : prev))
        break
      case 'Enter':
        e.preventDefault()
        if (selectedIndex >= 0 && voices[selectedIndex]) {
          handleVoiceSelect(voices[selectedIndex])
        }
        break
      case 'Escape':
        setIsOpen(false)
        break
    }
  }

  const handlePopoverOpenChange = (open: boolean) => {
    setIsOpen(open)
    if (open && searchInputRef.current) {
      // Focus input directly when opening
      setTimeout(() => searchInputRef.current?.focus(), 100)
    } else {
      setSelectedIndex(-1)
      setSearchQuery('')
      stopAllAudio()
    }
  }

  // Cleanup audio on unmount
  React.useEffect(() => {
    return () => {
      if (audioRef) {
        audioRef.pause()
        audioRef.currentTime = 0
      }
    }
  }, [])

  const getAccentFlag = (accent: string) => {
    const flags: Record<string, string> = {
      american: '🇺🇸',
      british: '🇬🇧',
      irish: '🇮🇪',
      australian: '🇦🇺',
      canadian: '🇨🇦',
      indian: '🇮🇳',
      south_african: '🇿🇦',
    }
    return flags[accent] || '🌍'
  }

  return (
    <Popover open={isOpen} onOpenChange={handlePopoverOpenChange}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="start">
        <div>
          <Input
            className="px-4"
            ref={searchInputRef}
            type="text"
            placeholder={placeholder}
            value={searchQuery}
            onChange={e => {
              const newQuery = e.target.value
              setSearchQuery(newQuery)
              setSelectedIndex(-1) // Reset selection immediately
              debouncedSearch(newQuery)
            }}
            variant="popover"
            onKeyDown={handleKeyDown}
          />
        </div>

        <div className="max-h-64 overflow-y-auto pt-1">
          {isLoading ? (
            <div className="p-4 text-sm text-muted-foreground">
              Searching voices...
            </div>
          ) : voices.length === 0 ? (
            <div className="p-4 text-sm text-muted-foreground">
              No voices found
            </div>
          ) : (
            voices.map((voice, index) => (
              <div
                key={voice.id}
                onClick={() => handleVoiceSelect(voice)}
                className={cn(
                  'flex items-center gap-2 px-1.5 py-1.5 mx-1.5 my-1.5 text-sm cursor-pointer transition-colors rounded-md',
                  'hover:bg-accent',
                  selectedVoice?.id === voice.id && 'bg-accent',
                  selectedIndex === index && 'bg-accent',
                )}
              >
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-8 w-8 p-0"
                  onClick={e => handlePlayPreview(e, voice)}
                  disabled={loadingVoiceId === voice.id}
                  title="Play voice sample"
                >
                  {loadingVoiceId === voice.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : playingVoiceId === voice.id ? (
                    <div className="h-4 w-4 bg-primary rounded-sm" />
                  ) : (
                    <Volume2 className="h-4 w-4" />
                  )}
                </Button>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium truncate">{voice.name}</span>
                    <span className="text-sm">
                      {getAccentFlag(voice.accent)}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-1">
                    <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                      {voice.labels.gender}
                    </span>
                    <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                      {voice.labels.age}
                    </span>
                    {voice.labels.use_case && (
                      <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                        {voice.labels.use_case}
                      </span>
                    )}
                  </div>
                  {voice.labels.description && (
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                      {voice.labels.description}
                    </p>
                  )}
                </div>

                {selectedVoice?.id === voice.id && (
                  <Check className="h-4 w-4 shrink-0" />
                )}
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
